import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { designer, featuredCases, philosophy, ui } from '../data/mock';
import { useHorizontalScroll } from '../hooks/useHorizontalScroll';

const Home = () => {
  const { wrapRef, trackRef, progress } = useHorizontalScroll();

  return (
    <div
      ref={wrapRef}
      className="relative h-screen w-screen md:overflow-hidden overflow-x-auto overflow-y-hidden snap-x md:snap-none snap-mandatory hide-scroll"
      style={{ background: 'var(--bg-dark)', scrollbarWidth: 'none' }}
    >
      {/* Slide track */}
      <div
        ref={trackRef}
        className="flex flex-row h-screen md:will-change-transform"
      >
        {/* Slide 1 — Hero */}
        <section className="relative shrink-0 w-screen md:h-screen h-screen flex items-center px-8 md:px-[90px] snap-start">
          <div className="max-w-6xl">
            <div className="font-label text-[0.72rem] text-[var(--terracotta)] mb-8">
              <span>————  Portfolio  /  2025</span>
            </div>
            <h1 className="font-display text-[clamp(3.5rem,11vw,11rem)] text-[var(--cream)]">
              <span className="block" style={{ animation: 'fadeUp 1.1s 0.2s both cubic-bezier(0.16, 1, 0.3, 1)' }}>Alifia</span>
              <span className="block" style={{ fontStyle: 'italic', fontWeight: 300, animation: 'fadeUp 1.1s 0.45s both cubic-bezier(0.16, 1, 0.3, 1)' }}>Povlsen</span>
            </h1>
            <p
              className="font-body mt-10 text-[var(--cream)]/70 max-w-xl text-base md:text-lg leading-relaxed"
              style={{ animation: 'fadeUp 1.1s 0.85s both cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              {designer.taglineShort}
            </p>
          </div>
          <div className="absolute bottom-10 left-8 md:left-24 font-label text-[0.66rem] text-[var(--cream)]/40 flex items-center gap-3" style={{ animation: 'fadeUp 1.1s 1.2s both' }}>
            <span className="hidden md:inline">{ui.scroll}</span>
            <span className="md:hidden">{ui.swipe}</span>
            <ArrowRight size={14} />
          </div>
          <div className="absolute right-8 md:right-24 bottom-10 font-label text-[0.66rem] text-[var(--cream)]/40">
            {designer.location.toUpperCase()}
          </div>
        </section>

        {/* Slide 2 — Philosophy */}
        <section className="relative shrink-0 w-screen h-screen flex items-center px-8 md:px-[90px] snap-start" style={{ background: 'linear-gradient(180deg, var(--bg-dark) 0%, #20120F 100%)' }}>
          <div className="absolute inset-0 opacity-30" style={{
            background: 'radial-gradient(circle at 70% 50%, rgba(179,84,62,0.18), transparent 60%), radial-gradient(circle at 20% 80%, rgba(74,107,130,0.18), transparent 55%), radial-gradient(circle at 50% 15%, rgba(232,179,60,0.07), transparent 50%)'
          }} />
          <div className="relative max-w-3xl">
            <div className="font-label text-[0.7rem] text-[var(--amber)] mb-10">01  /  Filosofi</div>
            <h2 className="font-display text-[clamp(2.5rem,6vw,5.5rem)] text-[var(--cream)] mb-10">
              {philosophy.heading}
            </h2>
            <p className="font-body text-[var(--cream)]/65 text-lg md:text-xl leading-relaxed max-w-2xl">
              {philosophy.body}
            </p>
          </div>
        </section>

        {/* Slides 3-5 — Featured Cases */}
        {featuredCases.map((c, i) => (
          <section key={c.id} className="relative shrink-0 w-screen h-screen flex items-center px-8 md:px-[90px] snap-start">
            <div className="grid md:grid-cols-12 gap-10 w-full items-center">
              <div className="md:col-span-5 order-2 md:order-1">
                <div className="font-label text-[0.7rem] mb-6" style={{ color: c.accent }}>
                  {String(i + 2).padStart(2, '0')}  /  Udvalgt
                </div>
                <h3 className="font-display text-[clamp(2rem,5vw,4.5rem)] text-[var(--cream)] mb-4">
                  {c.subtitle}
                </h3>
                <div className="font-label text-[0.7rem] text-[var(--cream)]/50 mb-6">
                  {c.title} — {c.year}
                </div>
                <p className="font-body text-[var(--cream)]/70 text-lg max-w-md leading-relaxed mb-10">
                  {c.descriptor}
                </p>
                <Link to={`/work/${c.slug}`} className="btn-terra inline-flex">
                  <span>{ui.viewCase}</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
              <div className="md:col-span-7 order-1 md:order-2 group relative overflow-hidden" style={{ aspectRatio: '4 / 5', maxHeight: '78vh' }}>
                <div
                  className="absolute inset-0 transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105"
                  style={{
                    backgroundImage: `url(${c.cover})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'brightness(0.85) contrast(1.04) saturate(0.95)'
                  }}
                />
                <div className="absolute inset-0 transition-opacity duration-700 opacity-0 group-hover:opacity-100" style={{ background: `linear-gradient(180deg, transparent 30%, ${c.accent}55 100%)` }} />
                <div className="absolute bottom-6 left-6 font-label text-[0.68rem] text-[var(--cream)]">
                  {c.category}
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Slide 6 — CTA */}
        <section className="relative shrink-0 w-screen h-screen flex items-center justify-center px-8 md:px-[90px] snap-start" style={{ background: 'linear-gradient(135deg, #1B0F0D 0%, #2A1F3D 100%)' }}>
          <div className="text-center max-w-4xl">
            <div className="font-label text-[0.7rem] text-[var(--amber)] mb-8">
              Afslutning  —  Lad os samarbejde
            </div>
            <h2 className="font-display text-[clamp(3rem,9vw,9rem)] text-[var(--cream)] leading-[0.95] mb-12">
              Lad os fortælle din<br />
              <span style={{ color: 'var(--terracotta)', fontStyle: 'italic', fontWeight: 300 }}>historie</span> sammen.
            </h2>
            <Link to="/contact" className="btn-terra">
              <span>{ui.getInTouch}</span>
              <ArrowRight size={14} />
            </Link>
            <div className="mt-16 font-label text-[0.66rem] text-[var(--cream)]/40">
              © {new Date().getFullYear()}  —  {designer.name}, {designer.location.split(',')[0]}
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .hide-scroll::-webkit-scrollbar { display: none; }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Progress indicator */}
      <div className="hidden md:block fixed bottom-8 right-8 z-[60] pointer-events-none">
        <div className="flex items-center gap-3">
          <div className="w-32 h-[1px] bg-[var(--cream)]/15 relative overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-[var(--terracotta)] transition-[width] duration-200"
              style={{ width: `${Math.round(progress * 100)}%` }}
            />
          </div>
          <div className="font-label text-[0.62rem] text-[var(--cream)]/60 tabular-nums">
            {String(Math.round(progress * 100)).padStart(2, '0')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
